import { useEffect, useRef } from 'react'
import { Animated, type GestureResponderEvent, Vibration } from 'react-native'

import { Chip, type IInputProps } from '~/components/chip'

import {
  DisplayNames,
  type ScopedProps,
  useInputMultiSelectContext,
} from '../../input-multi-select.context'

const SCALE_INITIAL = 0.9
const SCALE_FINAL = 1
const SPRING_SPEED = 10
const SPRING_BOUNCINESS = 8

const FADE_INITIAL = 0
const FADE_FINAL = 1
const FADE_DURATION = 300

type InputMultiSelectChiProps = Pick<
  IInputProps,
  'hiddenClearButton' | 'onLongPress'
> & {
  // O rótulo do chip
  label: string
}

export const InputMultiSelectChip: React.FC<
  InputMultiSelectChiProps
> = externalProps => {
  const { __scopeInputMultiSelect, label, onLongPress, ...props } =
    externalProps as ScopedProps<InputMultiSelectChiProps>

  const { size, isDisabled, isReadOnly } = useInputMultiSelectContext(
    DisplayNames.CHIP,
    __scopeInputMultiSelect
  )

  const chipSize = size !== 'md' ? 'md' : 'sm'

  const fadeAnim = useRef(new Animated.Value(FADE_INITIAL)).current
  const scaleAnim = useRef(new Animated.Value(SCALE_INITIAL)).current

  useEffect(() => {
    Animated.spring(scaleAnim, {
      toValue: SCALE_FINAL,
      speed: SPRING_SPEED,
      bounciness: SPRING_BOUNCINESS,
      useNativeDriver: true,
    }).start()

    Animated.timing(fadeAnim, {
      toValue: FADE_FINAL,
      duration: FADE_DURATION,
      useNativeDriver: true,
    }).start()
  }, [fadeAnim, scaleAnim])

  function onDeleteOption(event: GestureResponderEvent) {
    onLongPress?.(event)
    Vibration.vibrate()
  }

  const displayClearButton = isDisabled || isReadOnly

  return (
    <Animated.View
      style={{
        opacity: fadeAnim,
        transform: [
          {
            scale: scaleAnim,
          },
        ],
      }}
      testID="input-multi-select-(chip)"
    >
      <Chip.Input
        {...props}
        size={chipSize}
        onLongPress={onDeleteOption}
        disabled={isDisabled}
        readOnly={isReadOnly}
        hiddenClearButton={displayClearButton}
      >
        <Chip.Label
          disabled={isDisabled}
          readOnly={isReadOnly}
          numberOfLines={1}
        >
          {label}
        </Chip.Label>
      </Chip.Input>
    </Animated.View>
  )
}

InputMultiSelectChip.displayName = DisplayNames.CHIP
